import {
  createRootRoute,
  createRoute,
  createRouter,
} from '@tanstack/react-router';

import { RootRoute } from './routes/__root';
import { IndexRoute } from './routes/index';
import { LoginRoute } from './routes/login';
import { OnboardingRoute } from './routes/onboarding';
import { SettingsRoute } from './routes/settings';
import { CommandCenterRoute } from './routes/command-center';
import { LifecycleRoute } from './routes/lifecycle';
import { ArtifactsRoute } from './routes/artifacts';
import { DependenciesRoute } from './routes/dependencies';
import { ActivityRoute } from './routes/activity';
import { ApprovalsRoute } from './routes/approvals';
import { DeployRoute } from './routes/deploy';
import { AuditRoute } from './routes/audit';
import { CostsRoute } from './routes/costs';
import { MarketingAdminRoute } from './routes/marketing';
import { DataResidencyRoute } from './routes/data-residency';
import { RateLimitsRoute } from './routes/rate-limits';
// glistening-zebra Phase F1 — UI consolidation. These two routes
// fill the only Tauri-panel intents not already covered elsewhere
// (Axon impact analysis + planning-side agent invocation), so the
// Tauri shell (F2) and VSCode webviews (F3) can iframe one
// canonical UI without losing capabilities.
import { ImpactRoute } from './routes/impact';
import { PlanningChatRoute } from './routes/planning-chat';

/**
 * Route registry.
 *
 * All seven routes (index, dashboard command-center, lifecycle, artifacts,
 * dependencies, activity, approvals) are built explicitly below via
 * `createRoute`. The file-based TanStack Router plugin is intentionally
 * bypassed — see `vite.config.ts` where the plugin is disabled so the
 * manually-registered tree is the single source of truth.
 *
 * Adding a new route = (1) import the component from `./routes/*`,
 * (2) declare `const fooRoute = createRoute({...})`, (3) append it to
 * `rootRoute.addChildren([...])`, (4) add a nav entry in `components/NavSidebar`.
 */
const rootRoute = createRootRoute({
  component: RootRoute,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: IndexRoute,
});

// H3.1 (round-4) — public /login page (AuthBoundary treats this as
// a bypass path so signed-out users can reach it).
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginRoute,
});

// K1.1 (round-5) — first-run 3-step onboarding wizard.
const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  component: OnboardingRoute,
});

// H3.3 (round-4) — settings page with Providers, API keys, and VCS
// credentials panels. Protected by AuthBoundary.
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/settings',
  component: SettingsRoute,
});

// K1.4 (round-5) — deploy panel (copy `helm install` commands, view artifacts).
const deployRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/deploy',
  component: DeployRoute,
});

// K5.2 (round-5) — audit trail view + evidence bundle export.
const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/audit',
  component: AuditRoute,
});

// K3.5 (round-5) — BYOK cost dashboard (reads /v1/billing/usage).
const costsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/costs',
  component: CostsRoute,
});

// L4.3 (round-5) — admin view of captured marketing leads.
const marketingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/marketing',
  component: MarketingAdminRoute,
});

// M4.1 (round-5) — read-only GDPR taint-map viewer.
const dataResidencyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/data-residency',
  component: DataResidencyRoute,
});

// T1 (v2.0) — per-API-key rolling rate-limit usage (admin-only).
const rateLimitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/rate-limits',
  component: RateLimitsRoute,
});

const dashboardIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: CommandCenterRoute,
});

const lifecycleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/lifecycle',
  component: LifecycleRoute,
});

const artifactsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/artifacts',
  component: ArtifactsRoute,
});

const dependenciesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/dependencies',
  component: DependenciesRoute,
});

const activityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/activity',
  component: ActivityRoute,
});

const approvalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/approvals',
  component: ApprovalsRoute,
});

// glistening-zebra Phase F1 — Axon blast-radius view. Sister of
// /dashboard/dependencies (which renders the static dep graph);
// this one runs a BFS impact analysis from a node key.
const impactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/impact',
  component: ImpactRoute,
});

// glistening-zebra Phase F1 — planning-side composer for agent
// invocation. Reads the live agent registry, dispatches the
// invoke endpoint, hands off to /dashboard/activity for the
// streaming view.
const planningChatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/planning-chat',
  component: PlanningChatRoute,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  onboardingRoute,
  dashboardIndexRoute,
  lifecycleRoute,
  artifactsRoute,
  dependenciesRoute,
  activityRoute,
  approvalsRoute,
  impactRoute,
  planningChatRoute,
  settingsRoute,
  deployRoute,
  auditRoute,
  costsRoute,
  marketingRoute,
  dataResidencyRoute,
  rateLimitsRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 30_000,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
